import { FC, useState } from 'react';

import Bugsnag from '@bugsnag/browser';
import { IconDownload } from '@tabler/icons-react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import { Button, LoadingIndicator } from '@cfa/react-core';

import useAuth from '@/hooks/useAuth';

import { queryClient } from '@/services/api';

import { setAuthCookieOptions } from '@/_generated/api-clients/content';
import { appInitializedAtom } from '@/app/state';

interface GetPdfButtonProps {
  pdfSrc: string;
}

/** Allow the new tab to load the blob before revoking the object URL. */
const BLOB_URL_REVOKE_DELAY_MS = 60_000;

const isDocumentDownloadReady = (
  isAppInitialized: boolean,
  isAuthSuccess: boolean,
): boolean => window.native.isNative || (isAppInitialized && isAuthSuccess);

const refreshWebDocumentCookies = async (): Promise<void> => {
  if (window.native.isNative) {
    return;
  }

  await queryClient.fetchQuery(setAuthCookieOptions({ throwOnError: true }));
};

const GetPdfButton: FC<GetPdfButtonProps> = ({ pdfSrc }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const isAppInitialized = useAtomValue(appInitializedAtom);
  const { isSuccess: isAuthSuccess } = useAuth();
  const isReady = isDocumentDownloadReady(isAppInitialized, isAuthSuccess);

  const handlePress = async () => {
    if (!isReady) {
      return;
    }

    setIsLoading(true);
    try {
      await refreshWebDocumentCookies();

      const res = await fetch(pdfSrc, { credentials: 'include' });
      if (!res.ok) {
        throw new Error(`PDF fetch failed with status ${res.status}`);
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(
        new Blob([blob], { type: 'application/pdf' }),
      );
      window.open(url, '_blank');
      window.setTimeout(
        () => URL.revokeObjectURL(url),
        BLOB_URL_REVOKE_DELAY_MS,
      );
    } catch (error) {
      Bugsnag.notify(new Error('Error getting PDF', { cause: error }));
      console.error('Error getting PDF:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      color="secondary"
      className="w-fit min-w-[200px]!"
      isDisabled={isLoading || !isReady}
      onPress={handlePress}
    >
      <span className="inline-flex items-center gap-2">
        {isLoading ? <LoadingIndicator size="sm" /> : <IconDownload />}
        <span className="notranslate" translate="no">
          {t('Button.getPdf')}
        </span>
      </span>
    </Button>
  );
};

export default GetPdfButton;
